// Iter125 · P1 Fase 7 · Día del Evento = ejecución real.
// Sin capturar nada nuevo. Consume exclusivamente los datos ya generados en
// las fases previas del expediente (Evento, Orden Maestra, Itinerario, Equipo,
// Producción y Faltantes/Compras). NO inventa tareas, horarios ni personas.
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import {
  ArrowLeft, MessageCircle, AlertTriangle, User, Truck, CheckCircle2,
  Circle, Zap, MapPin, ClipboardList, ChefHat, ShoppingCart, ExternalLink,
  Clock,
} from "lucide-react";

const norm = (s) => (s || "").trim().toLowerCase();

const ModoEvento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [evento, setEvento] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [ordenMaestra, setOrdenMaestra] = useState([]);
  const [produccion, setProduccion] = useState([]);
  const [faltantes, setFaltantes] = useState([]);
  const [comprasPend, setComprasPend] = useState([]);
  const [hora, setHora] = useState(new Date());
  const [notaNueva, setNotaNueva] = useState("");
  const [guardandoNota, setGuardandoNota] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ev, om, prod, falt, comp] = await Promise.all([
        axios.get(`${API}/eventos/${id}`),
        axios.get(`${API}/eventos/${id}/orden-maestra?incluir_derivadas=true`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/eventos/${id}/produccion-items`).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/eventos/${id}/faltantes`).catch(() => ({ data: { faltantes: [] } })),
        axios.get(`${API}/compras?evento_id=${id}`).catch(() => ({ data: [] })),
      ]);
      const e = ev.data.evento || ev.data;
      setEvento(e);
      setOrdenMaestra(om.data?.items || []);
      setProduccion(prod.data?.items || []);
      setFaltantes(falt.data?.faltantes || []);
      setComprasPend((comp.data || []).filter(c => c.estatus === "pendiente" || c.estatus === "comprado"));
      if (e.cliente_id) {
        try {
          const c = await axios.get(`${API}/clientes/${e.cliente_id}`);
          setCliente(c.data.cliente || c.data);
        } catch { /* ignore */ }
      }
    } catch {
      toast.error("No se pudo cargar el día del evento");
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const t = setInterval(() => setHora(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // ===== Fuentes reales =====
  const personalAsignados = evento?.personal_asignado || [];
  const proveedoresAsignados = useMemo(() => {
    const raw = evento?.proveedores_asignados || [];
    return raw.map(x => (typeof x === "string" ? { proveedor_id: x } : x));
  }, [evento?.proveedores_asignados]);

  // Cronograma unificado: consume la lista final que ya entrega el backend
  // (`orden-maestra?incluir_derivadas=true` fusiona OM + itinerario del evento
  // con dedup por ref_key). NO volvemos a sumar `evento.itinerario` aquí para
  // evitar duplicaciones (LLEGAN MESEROS, BIENVENIDA, BOTANAS, FIN aparecían 2x).
  const cronograma = useMemo(() => {
    return (ordenMaestra || [])
      .filter(o => (o.hora || "").trim() && (o.actividad || "").trim())
      .map(o => ({
        id: o.origen === "derivada:itinerario" ? `iti_${o.ref_key || o.actividad}` : `om_${o.item_id}`,
        om_id: o.item_id,
        origen: o.origen === "derivada:itinerario" ? "itinerario" : "orden_maestra",
        hora: o.hora,
        actividad: o.actividad,
        responsable: o.responsable || "",
        estado: o.estado || "pendiente",
      }))
      .sort((a, b) => (a.hora || "").localeCompare(b.hora || ""));
  }, [ordenMaestra]);

  const ahoraHM = `${String(hora.getHours()).padStart(2, "0")}:${String(hora.getMinutes()).padStart(2, "0")}`;
  const proxima = cronograma.find(c => c.estado !== "completado" && (c.hora >= ahoraHM));
  const enCurso = cronograma.filter(c => c.hora <= ahoraHM && c.estado !== "completado").slice(-1)[0];
  const completadas = cronograma.filter(c => c.estado === "completado").length;
  const progreso = cronograma.length ? (completadas / cronograma.length) * 100 : 0;

  // Pendientes operativos (OM sin hora también aplica)
  const omPendientes = (ordenMaestra || []).filter(o => o.estado === "pendiente" || o.estado === "en_proceso");

  const prodPendientes = (produccion || []).filter(p =>
    ["pendiente", "en_preparacion", "terminado", "empacado", "cargado"].includes(p.estado)
  );

  const faltantesReales = (faltantes || []).filter(f => (f.faltante || 0) > 0 || f.estado === "no_registrado_inventario");

  const wa = (cliente?.whatsapp || "").replace(/\D/g, "");
  const telWA = wa.length === 10 ? `52${wa}` : wa;

  // ===== Acciones que reutilizan endpoints existentes =====
  const marcarOMCompletada = async (om_id) => {
    try {
      await axios.post(`${API}/eventos/${id}/orden-maestra/${om_id}/estado`, { accion: "completar" });
      toast.success("Tarea completada");
      fetchData();
    } catch {
      toast.error("No se pudo completar");
    }
  };
  const cambiarEstadoProduccion = async (prodId, nuevoEstado) => {
    try {
      await axios.put(`${API}/eventos/${id}/produccion-items/${prodId}`, { estado: nuevoEstado });
      toast.success(`Producción → ${nuevoEstado}`);
      fetchData();
    } catch {
      toast.error("No se pudo actualizar producción");
    }
  };
  // Iter151 · notas puede venir como string (histórico) o como lista de objetos
  // {fecha, autor, texto} (seed nuevos). Normalizamos a string sólo para lectura.
  const notasTexto = useMemo(() => {
    const n = evento?.notas;
    if (!n) return "";
    if (typeof n === "string") return n;
    if (Array.isArray(n)) {
      return n.map(x => {
        if (typeof x === "string") return x;
        const f = (x?.fecha || "").slice(0, 16).replace("T", " ");
        const a = x?.autor ? `[${x.autor}] ` : "";
        return `${f ? `[${f}] ` : ""}${a}${x?.texto || ""}`.trim();
      }).filter(Boolean).join("\n");
    }
    return "";
  }, [evento?.notas]);

  const guardarNota = async () => {
    if (!notaNueva.trim()) return;
    setGuardandoNota(true);
    try {
      const stamp = new Date().toLocaleString("es-MX", { hour: "2-digit", minute: "2-digit" });
      const nuevas = `[${stamp}] ${notaNueva.trim()}\n${notasTexto}`.trim();
      await axios.put(`${API}/eventos/${id}`, { notas: nuevas });
      setNotaNueva("");
      toast.success("Nota agregada");
      fetchData();
    } catch {
      toast.error("No se pudo guardar la nota");
    } finally {
      setGuardandoNota(false);
    }
  };

  const PROD_SIGUIENTE = {
    pendiente: "en_preparacion", en_preparacion: "terminado",
    terminado: "empacado", empacado: "cargado", cargado: "entregado",
  };

  return (
    <div className="min-h-screen -m-4 sm:-m-6 md:-m-8" style={{ background: `radial-gradient(circle at top left, var(--brand-primary-soft), var(--brand-surface) 60%)` }}>
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* TOP */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(`/panel/eventos/${id}`)} className="eo-btn eo-btn-outline" data-testid="btn-salir-modo-evento">
            <ArrowLeft className="w-4 h-4" /> Salir
          </button>
          <span className="eo-pill eo-pill-primary inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--brand-primary)" }} />
            DÍA DEL EVENTO · En vivo
          </span>
        </div>

        {/* HORA + AHORA / SIGUIENTE */}
        <div className="eo-card p-6 text-center eo-animate-in" data-testid="me-header">
          <p className="font-display text-6xl md:text-7xl leading-none" style={{ color: "var(--brand-text)" }}>
            {hora.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
          </p>
          <p className="text-[11px] uppercase tracking-[0.18em] mt-2" style={{ color: "var(--brand-text-muted)" }}>
            {hora.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}
          </p>
          {evento && (
            <p className="text-sm mt-3" style={{ color: "var(--brand-text)" }}>
              <Zap className="inline w-4 h-4 mr-1" style={{ color: "var(--brand-primary)" }} />
              {evento.nombre} · {cliente?.nombre || ""}
              {(evento.ubicacion || evento.direccion) && <span className="ml-2 text-xs" style={{ color: "var(--brand-text-muted)" }}><MapPin className="inline w-3 h-3" /> {evento.ubicacion || evento.direccion}</span>}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="p-3 rounded-2xl text-left" style={{ background: "var(--brand-warning-soft)", border: "1px solid rgba(217,119,6,0.2)" }} data-testid="me-en-curso">
              <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-warning)" }}>En curso</p>
              {enCurso ? (
                <>
                  <p className="font-display text-lg mt-1" style={{ color: "var(--brand-text)" }}>{enCurso.hora} · {enCurso.actividad}</p>
                  <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{enCurso.responsable || "Sin responsable"}</p>
                </>
              ) : (
                <p className="text-sm italic" style={{ color: "var(--brand-text-muted)" }}>Ninguna en curso</p>
              )}
            </div>
            <div className="p-3 rounded-2xl text-left" style={{ background: "var(--brand-primary-soft)", border: "1px solid rgba(214,95,121,0.2)" }} data-testid="me-siguiente">
              <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-primary)" }}>Siguiente</p>
              {proxima ? (
                <>
                  <p className="font-display text-lg mt-1" style={{ color: "var(--brand-primary-dark)" }}>{proxima.hora} · {proxima.actividad}</p>
                  <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{proxima.responsable || "Sin responsable"}</p>
                </>
              ) : (
                <p className="text-sm italic" style={{ color: "var(--brand-text-muted)" }}>Sin próximas actividades</p>
              )}
            </div>
          </div>
          {cronograma.length > 0 && (
            <div className="mt-3">
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--brand-surface-muted)" }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progreso}%`, background: `linear-gradient(90deg, var(--brand-primary), var(--brand-success))` }} />
              </div>
              <p className="text-[10px] mt-1" style={{ color: "var(--brand-text-muted)" }}>{completadas} de {cronograma.length} completadas</p>
            </div>
          )}
        </div>

        {/* QUICK ACTIONS */}
        <div className="grid grid-cols-3 gap-2">
          {telWA && (
            <a href={`https://wa.me/${telWA}`} target="_blank" rel="noopener noreferrer" className="eo-card eo-card-hover p-3 text-center" data-testid="btn-cliente">
              <div className="w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-1" style={{ background: "var(--brand-success-soft)" }}>
                <MessageCircle className="w-5 h-5" style={{ color: "var(--brand-success)" }} />
              </div>
              <p className="text-xs font-medium" style={{ color: "var(--brand-text)" }}>Cliente</p>
            </a>
          )}
          <button onClick={() => navigate(`/panel/eventos/${id}?tab=personal_prov`)} className="eo-card eo-card-hover p-3 text-center" data-testid="btn-equipo">
            <div className="w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-1" style={{ background: "var(--brand-info-soft)" }}>
              <User className="w-5 h-5" style={{ color: "var(--brand-info)" }} />
            </div>
            <p className="text-xs font-medium" style={{ color: "var(--brand-text)" }}>Equipo</p>
          </button>
          <button onClick={() => navigate(`/panel/eventos/${id}?tab=itinerario_od`)} className="eo-card eo-card-hover p-3 text-center" data-testid="btn-itinerario">
            <div className="w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-1" style={{ background: "var(--brand-accent-soft)" }}>
              <Clock className="w-5 h-5" style={{ color: "var(--brand-accent)" }} />
            </div>
            <p className="text-xs font-medium" style={{ color: "var(--brand-text)" }}>Itinerario</p>
          </button>
        </div>

        {/* CRONOGRAMA EN VIVO desde fuentes reales */}
        <div className="eo-card p-5" data-testid="me-cronograma">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>Cronograma</h2>
            <button onClick={() => navigate(`/panel/eventos/${id}?tab=itinerario_od`)} className="text-[11px] underline" style={{ color: "var(--brand-primary)" }}>Ver itinerario →</button>
          </div>
          {cronograma.length === 0 ? (
            <p className="text-sm italic text-center py-3" style={{ color: "var(--brand-text-muted)" }}>Sin actividades con hora. Define horas en Orden Maestra o Itinerario.</p>
          ) : (
            <div className="space-y-1.5">
              {cronograma.map(t => (
                <div
                  key={t.id}
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-left text-sm"
                  style={{
                    background: t.estado === "completado" ? "var(--brand-success-soft)" : "transparent",
                    border: `1px solid ${t.estado === "completado" ? "rgba(46,143,90,0.2)" : "var(--brand-border)"}`,
                  }}
                  data-testid={`me-cron-${t.id}`}
                >
                  <button
                    onClick={() => t.origen === "orden_maestra" && marcarOMCompletada(t.om_id)}
                    disabled={t.origen !== "orden_maestra" || t.estado === "completado"}
                    className="shrink-0"
                    title={t.origen === "orden_maestra" ? "Marcar como completada" : "Solo se puede completar desde Orden Maestra"}
                  >
                    {t.estado === "completado"
                      ? <CheckCircle2 className="w-5 h-5" style={{ color: "var(--brand-success)" }} />
                      : <Circle className="w-5 h-5" style={{ color: "var(--brand-text-muted)" }} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium" style={{ color: "var(--brand-text)", textDecoration: t.estado === "completado" ? "line-through" : "none" }}>
                      <span className="font-mono mr-2">{t.hora}</span>
                      {t.actividad}
                    </p>
                    <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>
                      {t.responsable || "Sin responsable"} · <span className="italic">{t.origen === "orden_maestra" ? "Orden Maestra" : "Itinerario"}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PENDIENTES OPERATIVOS (OM sin hora + con hora no completadas) */}
        <div className="eo-card p-5" data-testid="me-pendientes-om">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl flex items-center gap-2" style={{ color: "var(--brand-text)" }}><ClipboardList className="w-5 h-5" /> Pendientes operativos ({omPendientes.length})</h2>
            <button onClick={() => navigate(`/panel/eventos/${id}?tab=orden_maestra`)} className="text-[11px] underline" style={{ color: "var(--brand-primary)" }}>Ver Orden Maestra →</button>
          </div>
          {omPendientes.length === 0 ? (
            <p className="text-sm italic text-center py-3" style={{ color: "var(--brand-text-muted)" }}>Sin pendientes.</p>
          ) : (
            <ul className="space-y-1.5">
              {omPendientes.slice(0, 15).map(o => (
                <li key={o.item_id} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid={`me-om-${o.item_id}`}>
                  <button onClick={() => marcarOMCompletada(o.item_id)} className="shrink-0" title="Marcar completada">
                    <Circle className="w-4 h-4" style={{ color: "var(--brand-text-muted)" }} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>{o.actividad}</p>
                    <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{o.area || "—"} · {o.responsable || "Sin responsable"}{o.hora ? ` · ${o.hora}` : ""}</p>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: o.estado === "en_proceso" ? "var(--brand-warning-soft)" : "var(--brand-primary-soft)", color: o.estado === "en_proceso" ? "var(--brand-warning)" : "var(--brand-primary-dark)" }}>{o.estado}</span>
                </li>
              ))}
              {omPendientes.length > 15 && (
                <li className="text-[11px] italic text-center pt-2" style={{ color: "var(--brand-text-muted)" }}>+ {omPendientes.length - 15} pendientes más en Orden Maestra</li>
              )}
            </ul>
          )}
        </div>

        {/* EQUIPO */}
        <div className="eo-card p-5" data-testid="me-equipo">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl flex items-center gap-2" style={{ color: "var(--brand-text)" }}><User className="w-5 h-5" /> Equipo del día</h2>
            <button onClick={() => navigate(`/panel/eventos/${id}?tab=personal_prov`)} className="text-[11px] underline" style={{ color: "var(--brand-primary)" }}>Ver todo →</button>
          </div>
          {(personalAsignados.length + proveedoresAsignados.length) === 0 ? (
            <p className="text-sm italic text-center py-3" style={{ color: "var(--brand-text-muted)" }}>Sin equipo asignado.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {personalAsignados.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-text-muted)" }}>Personal · {personalAsignados.length}</p>
                  <ul className="space-y-1 text-xs">
                    {personalAsignados.map(p => {
                      const tel = (p.whatsapp || p.telefono || "").replace(/\D/g, "");
                      const wa2 = tel.length === 10 ? `52${tel}` : tel;
                      return (
                        <li key={p.personal_id} className="flex items-center gap-2 p-1.5 rounded" style={{ background: "var(--brand-surface-muted)" }} data-testid={`me-pers-${p.personal_id}`}>
                          <span className="flex-1 truncate"><b>{p.nombre}</b> <span style={{ color: "var(--brand-text-muted)" }}>· {p.rol || "—"}{p.horario ? ` · ${p.horario}` : ""}</span></span>
                          {wa2 && (
                            <a href={`https://wa.me/${wa2}`} target="_blank" rel="noopener noreferrer" className="p-1"><MessageCircle className="w-3 h-3" style={{ color: "var(--brand-success)" }} /></a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {proveedoresAsignados.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-text-muted)" }}>Proveedores · {proveedoresAsignados.length}</p>
                  <ul className="space-y-1 text-xs">
                    {proveedoresAsignados.map(pv => (
                      <li key={pv.proveedor_id} className="flex items-center gap-2 p-1.5 rounded" style={{ background: "var(--brand-surface-muted)" }} data-testid={`me-prov-${pv.proveedor_id}`}>
                        <Truck className="w-3 h-3 shrink-0" style={{ color: "var(--brand-accent)" }} />
                        <span className="flex-1 truncate"><b>{pv.nombre || pv.proveedor_id}</b> <span style={{ color: "var(--brand-text-muted)" }}>{pv.servicio ? `· ${pv.servicio}` : ""}{pv.llegada ? ` · llega ${pv.llegada}` : ""}</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* PRODUCCIÓN */}
        {prodPendientes.length > 0 && (
          <div className="eo-card p-5" data-testid="me-produccion">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl flex items-center gap-2" style={{ color: "var(--brand-text)" }}><ChefHat className="w-5 h-5" /> Producción ({prodPendientes.length})</h2>
              <button onClick={() => navigate(`/panel/eventos/${id}?tab=produccion`)} className="text-[11px] underline" style={{ color: "var(--brand-primary)" }}>Ver todo →</button>
            </div>
            <ul className="space-y-1.5">
              {prodPendientes.slice(0, 12).map(p => (
                <li key={p.produccion_item_id} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }} data-testid={`me-prod-${p.produccion_item_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>{p.preparacion}</p>
                    <p className="text-[11px]" style={{ color: "var(--brand-text-muted)" }}>{p.cantidad_vigente} {p.unidad}{p.responsable ? ` · ${p.responsable}` : ""}</p>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "var(--brand-primary-soft)", color: "var(--brand-primary-dark)" }}>{p.estado}</span>
                  {PROD_SIGUIENTE[p.estado] && (
                    <button onClick={() => cambiarEstadoProduccion(p.produccion_item_id, PROD_SIGUIENTE[p.estado])} className="text-[10px] underline" style={{ color: "var(--brand-primary)" }} data-testid={`me-prod-avanzar-${p.produccion_item_id}`}>
                      → {PROD_SIGUIENTE[p.estado]}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* COMPRAS / FALTANTES */}
        {(faltantesReales.length > 0 || comprasPend.length > 0) && (
          <div className="eo-card p-5" data-testid="me-compras">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl flex items-center gap-2" style={{ color: "var(--brand-text)" }}><ShoppingCart className="w-5 h-5" /> Compras / Entregas</h2>
              <button onClick={() => navigate(`/panel/eventos/${id}?tab=produccion`)} className="text-[11px] underline" style={{ color: "var(--brand-primary)" }}>Faltantes en Producción →</button>
            </div>
            {faltantesReales.length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-warning)" }}>Faltantes en Inventario · {faltantesReales.length}</p>
                <ul className="text-xs space-y-1">
                  {faltantesReales.slice(0, 10).map((f, i) => (
                    <li key={`fi-${i}`} className="flex justify-between p-1.5 rounded" style={{ background: "var(--brand-warning-soft)" }} data-testid={`me-fi-${i}`}>
                      <span>{f.producto}</span>
                      <span className="font-medium">{f.faltante ?? "—"} {f.unidad}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {comprasPend.length > 0 && (
              <div>
                <p className="text-[10px] uppercase mb-1" style={{ color: "var(--brand-primary)" }}>Compras · {comprasPend.length}</p>
                <ul className="text-xs space-y-1">
                  {comprasPend.slice(0, 10).map(c => (
                    <li key={c.compra_id} className="flex items-center gap-2 p-1.5 rounded" style={{ background: "var(--brand-surface-muted)" }} data-testid={`me-compra-${c.compra_id}`}>
                      <span className="flex-1 truncate">{c.producto}</span>
                      <span style={{ color: "var(--brand-text-muted)" }}>{c.cantidad} {c.unidad}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: c.estatus === "comprado" ? "var(--brand-success-soft)" : "var(--brand-warning-soft)", color: c.estatus === "comprado" ? "var(--brand-success)" : "var(--brand-warning)" }}>{c.estatus}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* INCIDENCIAS / NOTAS (reutiliza evento.notas existente) */}
        <div className="eo-card p-5" data-testid="me-incidencias">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl flex items-center gap-2" style={{ color: "var(--brand-text)" }}><ClipboardList className="w-5 h-5" /> Incidencias / notas del día</h2>
          </div>
          <div className="flex gap-2 mb-3">
            <input
              value={notaNueva}
              onChange={(e) => setNotaNueva(e.target.value)}
              placeholder="Registrar una incidencia u observación…"
              className="eo-input flex-1 text-sm"
              data-testid="me-nota-input"
            />
            <button onClick={guardarNota} disabled={guardandoNota || !notaNueva.trim()} className="eo-btn eo-btn-primary text-xs" data-testid="me-nota-guardar">Guardar</button>
          </div>
          {notasTexto.trim() ? (
            <pre className="text-xs whitespace-pre-wrap max-h-40 overflow-y-auto p-2 rounded" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }} data-testid="me-notas-hist">{notasTexto}</pre>
          ) : (
            <p className="text-xs italic" style={{ color: "var(--brand-text-muted)" }}>Sin incidencias registradas.</p>
          )}
        </div>

        {/* EMERGENCIA */}
        {telWA && (
          <button onClick={() => window.open(`https://wa.me/${telWA}?text=${encodeURIComponent('EMERGENCIA: necesito comunicarme contigo urgente.')}`, '_blank')} className="w-full eo-btn h-14 text-base" style={{ background: "var(--brand-danger)", color: "#fff" }} data-testid="btn-emergencia">
            <AlertTriangle className="w-5 h-5" /> Emergencia
          </button>
        )}

        <button onClick={() => navigate(`/panel/eventos/${id}`)} className="w-full eo-btn eo-btn-outline text-sm" data-testid="btn-volver-expediente">
          <ExternalLink className="w-4 h-4" /> Volver al expediente
        </button>
      </div>
    </div>
  );
};

export default ModoEvento;
